import { useEffect, useRef, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, redirect } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import ShopIcon from '@mui/icons-material/Shop';
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { useSelector } from "react-redux";

// import Inbox from "components/Inbox/Inbox";
import { axiosClient } from "Api/axoisClient";
import { Logo_white } from "constants/images";
import { Box, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";


function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const {t} = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const isLarge = useMediaQuery("(min-width:1200px)");
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const { isAdmin } = useSelector((state) => state.auth);
  const collapseName = location.pathname.replace("/", "");

  let textColor = "white";

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [thumb, setThumb] = useState({ left: 0, width: 100 });

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    // custom scrollbar: thumb size/position as % of the track
    const width = Math.min(100, (el.clientWidth / el.scrollWidth) * 100);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const left = maxScroll > 0 ? (el.scrollLeft / maxScroll) * (100 - width) : 0;
    setThumb({ left, width });
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({
      left: -200,
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({
      left: 200,
      behavior: "smooth",
    });
  };

  // Mobile: keep arrows state in sync and bring the active item into view
  useEffect(() => {
    if (isLarge) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const active = el.querySelector("a.active");
    if (active) {
      el.scrollTo({
        left: active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2,
        behavior: "smooth",
      });
    }
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [isLarge, location.pathname]);

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route, belongToAdmin }, index) => {
    
    let returnValue;
    
    if (type === "collapse" && (belongToAdmin === isAdmin || belongToAdmin === undefined )) {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  // const redirectTo = async (e) => {
  //   e.preventDefault()
    
  //   try {
  //     const res = await axiosClient.post('/redirect-to', {
  //       url : `${process.env.REACT_APP_MARKET_PATH}/login-with-session`
  //     })

  //     if(res.status === 200){

  //       console.log(res.data.url);
  //       window.open(res.data.url, "_blank");

  //     }

  //   } catch (error) {
      
  //     if(error.response.status === (422 || 500)){
  //       Swal.fire({
  //         icon: "error",
  //         title: t("modals.command.messages.error.title"),
  //         text: t("modals.command.messages.error.text"),
  //       });
  //     }

  //   }

  // }

  // const redirectTo = async (e) => {
  // e.preventDefault();
  
  // try {
  //   // 1. كنجيبو التوكن اللي مسجل دابا في الداشبورد
  //   const token = localStorage.getItem('token');

  //   const res = await axiosClient.post('/redirect-to', {
  //     url: `${process.env.REACT_APP_MARKET_PATH}/login-with-session`
  //   });

  //   if (res.status === 200) {
  //     let finalUrl = res.data.url;

  //     // 2. كنزيدو التوكن للرابط باش الماركت يقدر يقرأه
  //     if (token) {
  //       const separator = finalUrl.includes('?') ? '&' : '?';
  //       finalUrl = `${finalUrl}${separator}token=${token}`;
  //     }

  //     console.log("Redirecting to Market with token:", finalUrl);
  //     window.open(finalUrl, "_blank");
  //   }

  // } catch (error) {
  //   // تصحيح بسيط للشرط ديالك
  //   if (error.response && (error.response.status === 422 || error.response.status === 500)) {
  //     Swal.fire({
  //       icon: "error",
  //       title: t("modals.command.messages.error.title"),
  //       text: t("modals.command.messages.error.text"),
  //     });
  //   }
  // }
  // };
const redirectTo = async (e) => {
  if (e) e.preventDefault();
  
  try {
    const token = localStorage.getItem('token');

    const marketPath = process.env.REACT_APP_MARKET_PATH;

    console.log("Token current:", token);
    console.log("Market Path from env:", marketPath);

    if (token && marketPath) {
      // تنقية الرابط: كنحيدو أي سلاش زايد ونلصقو التوكن
      const cleanPath = marketPath.endsWith('/') ? marketPath : `${marketPath}/`;
      const finalUrl = `${cleanPath}?token=${token}`;

      console.log("🚀 Opening Market with:", finalUrl);
      
      // حل الرابط في نافذة جديدة
      window.open(finalUrl, "_blank");
    } else {
      console.error("Missing Token or Market Path! Token:", token, "Path:", marketPath);

    }
  } catch (error) {
    console.error("Redirect Error:", error);
  }
};
  return (
    <>
      {
        isLarge ? (
          <SidenavRoot
            {...rest}
            variant="permanent"
            ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
          >
            <MDBox pt={3} pb={1} px={4} textAlign="center">
              <MDBox
                display={{ xs: "block", xl: "none" }}
                position="absolute"
                top={0}
                right={0}
                p={1}
                onClick={closeSidenav}
                sx={{ cursor: "pointer" }}
              >
                <MDTypography variant="h6" color="secondary">
                  <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                </MDTypography>
              </MDBox>
              <MDBox component={NavLink} to="/" display="flex" alignItems="center">
                <MDBox component="img" src={Logo_white} alt="Brand" width="9rem" />
                {/* <MDBox
                  width={!brandName && "100%"}
                  sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
                >
                  <img src="/favicon.png" /> 
                  <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                    {brandName}
                  </MDTypography>
                </MDBox> */}
              </MDBox>
            </MDBox>
            <Divider
              key='ghdg'
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
            />
            <List>
              {renderRoutes}
      
              <MDBox className='relative' onClick={(e) => redirectTo(e)}>
                  <SidenavCollapse
                      name={t('goToMarget')}
                      icon={<ShopIcon/>}
                  />
              </MDBox>
              {/* <Inbox/> */}
      
            </List>
            {/* <MDBox p={2} mt="auto">
              <MDButton
                component="a"
                href="https://www.creative-tim.com/product/material-dashboard-pro-react"
                target="_blank"
                rel="noreferrer"
                variant="gradient"
                color={sidenavColor}
                fullWidth
              >
                upgrade to pro
              </MDButton>
            </MDBox> */}
          </SidenavRoot>
        ) :
          // <Box  className={`w-full ${darkMode ? 'bg-[#1f283e]' : ' bg-gradient-to-tr from-[#42424a] to-[#191919]' } rounded-lg mt-3 py-2 overflow-x-scroll flex items-center`}>
          //   {renderRoutes}
        
          //   <MDBox className='relative' onClick={(e) => redirectTo(e)}>
          //       <SidenavCollapse
          //           name={t('goToMarget')}
          //           icon={<ShopIcon/>}
          //       />
          //   </MDBox>
          // </Box>
          
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            mt: 1,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="scroll left"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            sx={{ ...arrowSx, background: darkMode ? '#1f283e' : '#2c2c31' }}
          >
            <Icon sx={{ fontSize: '22px !important' }}>chevron_left</Icon>
          </Box>

          <Box
            className={`${darkMode ? 'bg-[#1f283e]' : 'bg-gradient-to-tr from-[#42424a] to-[#191919]'} rounded-lg`}
            sx={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden', px: 0.5 }}
          >
            {/* fade edges to hint there is more content */}
            <Box sx={{ ...fadeSx, left: 0, opacity: canScrollLeft ? 1 : 0,
              background: `linear-gradient(to right, ${darkMode ? '#1f283e' : '#2c2c31'}, transparent)` }} />
            <Box sx={{ ...fadeSx, right: 0, opacity: canScrollRight ? 1 : 0,
              background: `linear-gradient(to left, ${darkMode ? '#1f283e' : '#232326'}, transparent)` }} />

            <Box
              ref={scrollRef}
              sx={{
                display: 'flex',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                pt: 1,
                pb: 0.5,
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                '& > *': { flexShrink: 0 },
                // hide the native scrollbar (mobile browsers auto-hide it), we draw our own below
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {renderRoutes}
              <MDBox className='relative' onClick={(e) => redirectTo(e)}>
                <SidenavCollapse name={t('goToMarget')} icon={<ShopIcon/>} />
              </MDBox>
            </Box>

            {/* always-visible yellow scrollbar */}
            <Box
              sx={{
                position: 'relative',
                height: '5px',
                mx: 1,
                mb: 0.75,
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.15)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${thumb.left}%`,
                  width: `${thumb.width}%`,
                  borderRadius: '10px',
                  background: '#fbc02d',
                }}
              />
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            aria-label="scroll right"
            onClick={scrollRight}
            disabled={!canScrollRight}
            sx={{ ...arrowSx, background: darkMode ? '#1f283e' : '#2c2c31' }}
          >
            <Icon sx={{ fontSize: '22px !important' }}>chevron_right</Icon>
          </Box>
        </Box>
      }
    </>
  );
}

const arrowSx = {
  cursor: 'pointer',
  width: '30px',
  height: '30px',
  p: 0,
  border: 'none',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'white',
  background: 'rgba(255,255,255,0.15)',
  transition: 'opacity .2s, background .2s',
  '&:hover:not(:disabled)': { color: '#fbc02d' },
  '&:disabled': { opacity: 0.25, cursor: 'default' },
};

const fadeSx = {
  position: 'absolute',
  top: 0,
  bottom: '14px',
  width: '24px',
  zIndex: 1,
  pointerEvents: 'none',
  transition: 'opacity .2s',
};

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
